class HeroBanner extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('.hero-banner__slider');
    this.slides = this.querySelectorAll('.hero-banner__slide');
    this.prevButton = this.querySelector('.slider-button--prev');
    this.nextButton = this.querySelector('.slider-button--next');
    this.dots = this.querySelectorAll('.slider-counter__link');
    this.currentSlide = 0;
    this.autoplay = this.dataset.autoplay === 'true';
    this.autoplaySpeed = parseInt(this.dataset.speed) * 1000 || 5000;
    this.autoplayTimer = null;
    
    if (this.slides.length > 0) {
      this.init();
    }
  }

  init() {
    
    // Set initial state
    this.updateSlide(0);

    // Add event listeners for controls
    if (this.prevButton) {
      this.prevButton.addEventListener('click', (e) => {
        e.preventDefault();
        this.previousSlide();
        this.pauseAutoplay();
      });
    }
    
    if (this.nextButton) {
      this.nextButton.addEventListener('click', (e) => {
        e.preventDefault();
        this.nextSlide();
        this.pauseAutoplay();
      });
    }

    // Dot navigation
    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', (e) => {
        e.preventDefault();
        const slideIndex = parseInt(dot.dataset.slideIndex) || index;
        this.goToSlide(slideIndex);
        this.pauseAutoplay();
      });
    });

    // Start autoplay if enabled
    if (this.autoplay && this.slides.length > 1) {
      this.startAutoplay();
      
      // Pause on hover
      this.addEventListener('mouseenter', () => this.pauseAutoplay());
      this.addEventListener('mouseleave', () => {
        if (this.autoplay) this.startAutoplay();
      });
      
      // Pause when page is not visible
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
          this.pauseAutoplay();
        } else if (this.autoplay) {
          this.startAutoplay();
        }
      });
    }

    // Keyboard navigation
    this.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') {
        this.previousSlide();
        this.pauseAutoplay();
      } else if (e.key === 'ArrowRight') {
        this.nextSlide();
        this.pauseAutoplay();
      }
    });

    // Touch/swipe support
    let touchStartX = 0;
    let touchEndX = 0;
    let isDragging = false;

    this.slider.addEventListener('touchstart', (e) => {
      touchStartX = e.changedTouches[0].screenX;
      isDragging = true;
    }, { passive: true });

    this.slider.addEventListener('touchmove', (e) => {
      if (!isDragging) return;
      touchEndX = e.changedTouches[0].screenX;
    }, { passive: true });

    this.slider.addEventListener('touchend', (e) => {
      if (!isDragging) return;
      isDragging = false;
      touchEndX = e.changedTouches[0].screenX;
      this.handleSwipe(touchStartX, touchEndX);
    });
  }

  updateSlide(index) {
    // Update dots
    this.dots.forEach((dot, i) => {
      dot.classList.toggle('is-active', i === index);
      dot.setAttribute('aria-current', i === index ? 'true' : 'false');
    });

    // Update slider position with transform
    if (this.slider) {
      const transformValue = `translateX(-${index * 100}%)`;
      this.slider.style.transform = transformValue;
    }

    // Update ARIA attributes for slides
    this.slides.forEach((slide, i) => {
      slide.setAttribute('aria-hidden', i !== index ? 'true' : 'false');
    });

    this.currentSlide = index;

    // Announce to screen readers
    this.announceSlide();
  }

  previousSlide() {
    const newIndex = this.currentSlide === 0 ? this.slides.length - 1 : this.currentSlide - 1;
    this.goToSlide(newIndex);
  }

  nextSlide() {
    const newIndex = this.currentSlide === this.slides.length - 1 ? 0 : this.currentSlide + 1;
    this.goToSlide(newIndex);
  }

  goToSlide(index) {
    if (index >= 0 && index < this.slides.length && index !== this.currentSlide) {
      this.updateSlide(index);
    }
  }

  startAutoplay() {
    this.pauseAutoplay();
    if (this.slides.length > 1) {
      this.autoplayTimer = setInterval(() => {
        this.nextSlide();
      }, this.autoplaySpeed);
    }
  }

  pauseAutoplay() {
    if (this.autoplayTimer) {
      clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }

  handleSwipe(startX, endX) {
    const threshold = 50;
    const diff = startX - endX;
    
    if (Math.abs(diff) > threshold) {
      if (diff > 0) {
        // Swiped left - next slide
        this.nextSlide();
      } else {
        // Swiped right - previous slide
        this.previousSlide();
      }
      this.pauseAutoplay();
    }
  }

  announceSlide() {
    const announcement = this.querySelector('[role="status"]');
    if (announcement) {
      announcement.textContent = `Slide ${this.currentSlide + 1} of ${this.slides.length}`;
    }
  }

  disconnectedCallback() {
    this.pauseAutoplay();
  }
}

// Define custom element
if (!customElements.get('hero-banner-component')) {
  customElements.define('hero-banner-component', HeroBanner);
}
